import React, { useMemo, useState } from "react";
import { App as AntdApp, Alert, Descriptions, Space, Tag, Typography, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import RestoreOptions, { DEFAULT_RESTORE_OPTIONS, buildIdsPayload } from "./RestoreOptions";
import RestoreResult from "./RestoreResult";
import { BACKUPS_ENDPOINT, RESTORE_MODE, formatBytes } from "../constants";

const { Text } = Typography;

/**
 * Restores a .kbak archive picked straight off the operator's own machine — a file this
 * server has never seen, from a laptop or another installation entirely.
 *
 * Dropping the file sends it up for inspection, so the table list is what the archive
 * actually holds. The upload is parked behind `stagingId` and handed back with the restore,
 * which is what keeps "look at it, then restore it" to a single transfer. Nothing is kept:
 * the file never enters the catalogue, and the staged copy is deleted the moment the
 * restore finishes.
 */
const UploadRestoreModal = ({ open, onClose, onDone }) => {
  const { mutate } = useMutations();
  const { modal } = AntdApp.useApp();

  const [fileName, setFileName] = useState(null);
  const [detail, setDetail] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [running, setRunning] = useState(false);
  const [options, setOptions] = useState(DEFAULT_RESTORE_OPTIONS);
  const [result, setResult] = useState(null);

  // The staged copy behind a result is already gone, so closing always starts the next
  // visit from a clean slate.
  const close = () => {
    setFileName(null);
    setDetail(null);
    setFailed(false);
    setResult(null);
    setOptions(DEFAULT_RESTORE_OPTIONS);
    onClose?.();
  };

  const inspect = async (file) => {
    setInspecting(true);
    setFailed(false);
    setDetail(null);
    setResult(null);
    setOptions(DEFAULT_RESTORE_OPTIONS);
    setFileName(file.name);

    const payload = new FormData();
    payload.append("file", file);

    await mutate("inspectUploadedBackup", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/upload/inspect`,
      payload,
      // The client pins Content-Type to application/json for every request, and axios
      // does not drop an instance-level default for FormData on its own — the endpoint
      // then refuses the JSON content type with a 415. Null removes the header so the
      // browser writes multipart/form-data with its boundary itself.
      config: { headers: { "Content-Type": null } },
      onSuccess: setDetail,
      // The toast already carries the reason; this only has to stop the dialog from
      // offering a restore of something it could not read.
      onError: () => setFailed(true),
    });

    setInspecting(false);
  };

  const tables = useMemo(
    () =>
      (detail?.tables || []).map((t) => ({
        name: t.name,
        rowCount: t.rowCount,
        supportsIdFilter: t.supportsIdFilter,
        disabled: !t.existsInTarget,
        disabledReason: "This table does not exist in the live database.",
      })),
    [detail]
  );

  const selectedNames = useMemo(
    () =>
      options.mode === RESTORE_MODE.FULL
        ? tables.filter((t) => !t.disabled).map((t) => t.name)
        : options.tables || [],
    [options.mode, options.tables, tables]
  );

  const runRestore = async () => {
    setRunning(true);
    setResult(null);

    await mutate("restoreUploadedBackup", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/upload/restore`,
      payload: {
        stagingId: detail?.stagingId,
        mode: options.mode,
        strategy: options.strategy,
        tables: options.mode === RESTORE_MODE.SELECTIVE ? options.tables : undefined,
        ids: buildIdsPayload(options, selectedNames),
        skipSafetyBackup: options.skipSafetyBackup,
      },
      onSuccess: (data) => {
        setResult(data);
        setDetail(null);
        onDone?.();
      },
      // A refused restore still rolls back a full result, which is worth showing.
      onError: (error) => {
        setResult(error?.response?.data?.data ?? error?.data ?? null);
        setDetail(null);
        onDone?.();
      },
    });

    setRunning(false);
  };

  const confirmRestore = () => {
    if (!detail) {
      modal.warning({
        title: "Nothing to restore",
        content: "Pick a backup archive first — it has to be read before it can be restored.",
      });
      return;
    }

    if (options.mode === RESTORE_MODE.SELECTIVE && selectedNames.length === 0) {
      modal.warning({ title: "Nothing selected", content: "Pick at least one table to restore." });
      return;
    }

    modal.confirm({
      title: "Restore this file?",
      centered: true,
      okText: "Restore",
      okType: "danger",
      content: (
        <span>
          {selectedNames.length} table{selectedNames.length === 1 ? "" : "s"} from{" "}
          <Text code>{fileName}</Text> will be written into the live database. This cannot be
          undone except by restoring another backup.
        </span>
      ),
      onOk: runRestore,
    });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Restore from an uploaded file"
      size="xl"
      confirmText={result ? "Close" : "Restore"}
      cancelText="Cancel"
      danger={!result}
      loading={running || (!result && inspecting)}
      onConfirm={result ? close : confirmRestore}
    >
      {result ? (
        <RestoreResult result={result} />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="The file is not stored anywhere"
            description="The upload is read once to run the restore and then thrown away — it does not appear in the archive list and nothing of it stays on the server."
          />

          <Upload.Dragger
            accept=".kbak"
            maxCount={1}
            showUploadList={false}
            disabled={inspecting || running}
            // Returning false stops antd from posting anywhere on its own — the inspect
            // call is the only thing that touches the network.
            beforeUpload={(file) => {
              inspect(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag a .kbak archive here</p>
            <p className="ant-upload-hint">
              {fileName ? (
                <>
                  Picked <Text code>{fileName}</Text> — drop another file to replace it.
                </>
              ) : (
                "An archive downloaded from this screen, or one taken on another installation."
              )}
            </p>
          </Upload.Dragger>

          {failed && (
            <Alert
              type="error"
              showIcon
              message="This file could not be read"
              description="It is not a backup archive this server can open. Pick a .kbak file taken by this application."
            />
          )}

          {detail && (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Taken">
                {detail.createdAt ? new Date(detail.createdAt).toLocaleString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Size">{formatBytes(detail.sizeBytes)}</Descriptions.Item>
              <Descriptions.Item label="Source">
                <Text code>{detail.sourceServer || "—"}</Text> / {detail.sourceDatabase || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contents">
                <Space size={6}>
                  <Tag>{detail.tables?.length ?? 0} tables</Tag>
                  <Tag>{(detail.rowCount ?? 0).toLocaleString()} rows</Tag>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          )}

          {(detail || inspecting) && (
            <RestoreOptions tables={tables} value={options} onChange={setOptions} loading={inspecting} />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default UploadRestoreModal;
